import { useCallback, useEffect } from 'react';

import { useDeviceDetect } from '@hooks/usedevicedetect';
// import useMutationObserver from './usemutationobserver';

const useToggleLiveChat = () => {
  // id of the live chat widget
  const liveChatId = 'Microsoft_Omnichannel_LCWidget_Chat_Iframe_Window';
  const { isMobile, isTablet } = useDeviceDetect();

  const setStyles = useCallback(() => {
    // Capture latest state of iFrame (for updated styles)
    const liveChatWidget: HTMLIFrameElement | null = document.getElementById(
      liveChatId
    ) as HTMLIFrameElement;

    if (!liveChatWidget) return;

    const isSmallDevice = isMobile || isTablet;

    if (!isSmallDevice && liveChatWidget) {
      liveChatWidget.style.bottom = '20px';
      return;
    }

    const quoteOrderBarElement = document.querySelector(
      '.quoteorderbar:has(> :nth-child(3):nth-last-child(1))'
    );

    // Depending on state of quote order bar, style as appropriate
    if (quoteOrderBarElement && quoteOrderBarElement.children.length === 3) {
      liveChatWidget.style.bottom =
        'calc(var(--config-layout-footer-gutter) + 16px)';
    } else {
      liveChatWidget.style.bottom =
        'calc(var(--config-layout-footer-gutter-single-row) + 16px)';
    }
  }, [liveChatId, isMobile, isTablet]);

  // Function to show chat
  const showElement = (): void => {
    const liveChatWidget: HTMLIFrameElement | null = document.getElementById(
      liveChatId
    ) as HTMLIFrameElement;
    if (liveChatWidget) {
      liveChatWidget.style.display = 'block';
    }
  };

  // Function to hide chat
  const hideElement = (): void => {
    const liveChatWidget: HTMLIFrameElement | null = document.getElementById(
      liveChatId
    ) as HTMLIFrameElement;
    if (liveChatWidget) {
      liveChatWidget.style.display = 'none';
    }
  };

  /* Function to listen for chat iframe resize. This is so we can apply
    our styles *after* Omnichannel, as they re-render a couple times which
    overwrites ours */
  const handleResizeObserver = (iframeElement: HTMLIFrameElement) => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setStyles();
        }
      }
    });

    resizeObserver.observe(iframeElement);

    return () => {
      resizeObserver.disconnect();
    };
  };

  // useEffect to apply styles when device type changes
  useEffect(() => {
    setStyles();
  }, [isMobile, isTablet, setStyles]);

  // useEffect to observe DOM mutations and handle chat iframe loading
  useEffect(() => {
    const observerCallback = (
      mutationsList: MutationRecord[],
      observer: MutationObserver
    ) => {
      for (let mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const element = document.getElementById(liveChatId);
          if (element) {
            handleResizeObserver(element as HTMLIFrameElement);
            observer.disconnect();
          } else {
          }
        }
      }
    };

    const mutationObserver = new MutationObserver(observerCallback);

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
    };
  }, [liveChatId, setStyles]);

  return {
    showElement,
    hideElement,
  };
};

export default useToggleLiveChat;
